import {
  Event,
  Country,
  getAllEntitiesLinkedToEvent,
  eventSummaryGeneration,
  Analysis,
} from "@unruly-osdk/sdk";
import { Client, Osdk } from "@osdk/client";
import { QueryClient, useQuery, UseQueryResult } from "@tanstack/react-query";
import { EntityItem } from "../common-types/Entity";
import { checkEnv } from "../helper/helper";
import { PublicEvent } from "@unruly-global/api-types";

const backendApiUrl = import.meta.env.VITE_BACKEND_API_URL;
checkEnv(backendApiUrl, "VITE_BACKEND_API_URL");
const publicEventStaleTimeInMs = import.meta.env
  .VITE_PUBLIC_EVENT_STALE_TIME_IN_MS;
checkEnv(publicEventStaleTimeInMs, "VITE_PUBLIC_EVENT_STALE_TIME_IN_MS");

const keys = {
  all: ["events"] as const,
  byId: (id: string | undefined) => [...keys.all, "id", id] as const,
  bySourcedEventIds: (ids: string[]) =>
    [...keys.all, "sourcedEventIds", [...ids].sort()] as const,
  country: (id: string | undefined) =>
    [...keys.all, "id", id, "entities", "country"] as const,
  entities: (id: string | undefined) =>
    [...keys.all, "id", id, "entities"] as const,
  context: (id: string | undefined) => [...keys.all, "context", id] as const,
  analysedInAnalysis: (analysisId: string) =>
    [...keys.all, "analysedInAnalysis", analysisId] as const,
  referencedInAnalysis: (analysisId: string) =>
    [...keys.all, "referencedInAnalysis", analysisId] as const,
  publicEventById: (eventId: string) =>
    [...keys.all, "publicEventById", eventId] as const,
};

function EventService(_queryClient: QueryClient, apiClient: Client) {
  const service = {
    useGetPublicEventById: (
      eventId: string,
    ): UseQueryResult<PublicEvent, unknown> => {
      return useQuery({
        queryKey: keys.publicEventById(eventId),
        queryFn: async () => service.getPublicEventByIdAsync(eventId),
        enabled: !!eventId,
        staleTime: Number(publicEventStaleTimeInMs),
      });
    },

    getPublicEventByIdAsync: async (eventId: string): Promise<PublicEvent> => {
      const response = await fetch(backendApiUrl + "/public-event/" + eventId, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = (await response.json()) as PublicEvent;
        return data;
      }
      throw new Error("Failed to fetch public event: " + response.statusText);
    },

    useGetEventById: (
      id: string | undefined,
    ): UseQueryResult<Osdk.Instance<Event>, unknown> => {
      return useQuery({
        queryKey: keys.byId(id),
        queryFn: async () => apiClient(Event).fetchOne(id!),
        enabled: !!id,
      });
    },

    useGetSourceEventsByIds: (
      ids: string[],
    ): UseQueryResult<
      Osdk.Instance<
        Event,
        never,
        | "id"
        | "name"
        | "source"
        | "sourceType"
        | "startDate"
        | "newsSourceUrl"
        | "eventSummary"
      >[],
      unknown
    > => {
      return useQuery({
        queryKey: keys.bySourcedEventIds(ids),
        queryFn: async () => {
          if (!ids || ids.length === 0) {
            return [];
          }

          const response = await apiClient(Event)
            .where({ id: { $in: ids } })
            .fetchPage({
              $select: [
                "id",
                "source",
                "sourceType",
                "name",
                "startDate",
                "newsSourceUrl",
                "eventSummary",
              ],
              $orderBy: { startDate: "desc" },
              $pageSize: ids.length,
            });
          return response.data;
        },
      });
    },

    useGetCountry: (
      event: Osdk.Instance<Event> | undefined,
    ): UseQueryResult<Osdk.Instance<Country> | null, unknown> => {
      return useQuery({
        queryKey: keys.country(event?.id),
        queryFn: async () => service.getCountryAsync(event!),
        enabled: !!event,
      });
    },

    getCountryAsync: async (
      event: Osdk.Instance<Event>,
    ): Promise<Osdk.Instance<Country> | null> => {
      if (!event.$link.primaryLocation) {
        return null;
      }
      try {
        const res = await event.$link.primaryLocation.fetchOneWithErrors();

        if (!res.error) {
          return res.value;
        }

        const locationCode = event.primaryEventLocation;
        if (locationCode && locationCode.length === 3) {
          const fallback = await apiClient(Country)
            .where({ isoAlpha3Code: locationCode })
            .fetchPage({ $pageSize: 1 });
          if (fallback.data.length > 0) {
            return fallback.data[0];
          }
        }

        return null;
      } catch (error) {
        console.error("Error fetching country for event:", error);
        throw error;
      }
    },

    getAllEntitiesLinkedToEvent: async (event: Osdk.Instance<Event>) => {
      return await apiClient(getAllEntitiesLinkedToEvent).executeFunction({
        event,
      });
    },

    useGetAllEntitiesLinkedToEvent: (
      event: Osdk.Instance<Event>,
    ): UseQueryResult<EntityItem[], unknown> => {
      return useQuery({
        queryKey: keys.entities(event.id),
        queryFn: async () =>
          (await service.getAllEntitiesLinkedToEvent(event)).map(parseEntity),
      });
    },

    getEventBroaderContext: async (event: Osdk.Instance<Event>) => {
      try {
        return await apiClient(eventSummaryGeneration).executeFunction({
          selectedEvent: event,
        });
      } catch (error) {
        console.error("Error fetching entities linked to event:", error);
        throw error;
      }
    },

    useGetEventBroaderContext: (
      event?: Osdk.Instance<Event>,
      fetchContextFromAPI?: boolean,
    ): UseQueryResult<string, unknown> => {
      return useQuery({
        queryKey: keys.context(event?.id),
        queryFn: async () => service.getEventBroaderContext(event!),
        enabled: !!event && fetchContextFromAPI,
      });
    },

    useGetEventsAnalysedInAnalysis: (
      analysisId: string,
    ): UseQueryResult<
      Osdk.Instance<
        Event,
        never,
        | "id"
        | "name"
        | "startDate"
        | "source"
        | "sourceType"
        | "newsSourceUrl"
        | "eventSummary"
      >[],
      unknown
    > => {
      return useQuery({
        queryKey: keys.analysedInAnalysis(analysisId),
        queryFn: async () => service.getEventsAnalysedInAnalysis(analysisId),
        enabled: !!analysisId,
      });
    },

    getEventsAnalysedInAnalysis: async (
      analysisId: string,
    ): Promise<
      Osdk.Instance<
        Event,
        never,
        | "id"
        | "name"
        | "startDate"
        | "source"
        | "sourceType"
        | "newsSourceUrl"
        | "eventSummary"
      >[]
    > => {
      const response = await apiClient(Analysis)
        .where({ id: analysisId })
        .pivotTo("analysedEvents")
        .fetchPage({
          $select: [
            "id",
            "name",
            "startDate",
            "source",
            "sourceType",
            "newsSourceUrl",
            "eventSummary",
          ],
        });

      return response.data;
    },

    useGetEventsReferencedInAnalysis: (
      analysisId: string,
    ): UseQueryResult<
      Osdk.Instance<
        Event,
        never,
        | "id"
        | "name"
        | "startDate"
        | "source"
        | "sourceType"
        | "newsSourceUrl"
        | "eventSummary"
      >[],
      unknown
    > => {
      return useQuery({
        queryKey: keys.referencedInAnalysis(analysisId),
        queryFn: async () => service.getEventsReferencedInAnalysis(analysisId),
        enabled: !!analysisId,
      });
    },

    getEventsReferencedInAnalysis: async (
      analysisId: string,
    ): Promise<
      Osdk.Instance<
        Event,
        never,
        | "id"
        | "name"
        | "startDate"
        | "source"
        | "sourceType"
        | "newsSourceUrl"
        | "eventSummary"
      >[]
    > => {
      const response = await apiClient(Analysis)
        .where({ id: analysisId })
        .pivotTo("referencedEvents")
        .fetchPage({
          $select: [
            "id",
            "name",
            "startDate",
            "source",
            "sourceType",
            "newsSourceUrl",
            "eventSummary",
          ],
        });

      return response.data;
    },
  };
  return service;
}

export default EventService;
function parseEntity(value: {
  id: string;
  name: string;
  type: string;
}): EntityItem {
  switch (value.type) {
    case "person":
    case "organization":
    case "country":
    case "event":
      return { id: value.id, name: value.name, type: value.type };
  }
  throw new Error(
    "Unkown linked entity type '" + value.type + "' for event " + value.id,
  );
}
